import { createBrowserRouter, redirect, RouteObject } from 'react-router-dom';
import Root from '@casops/routes/root/root';
import ErrorPageRouter from '@casops/templates/error-page-router/ErrorPageRouter';
import ProtectedRoute from '@casops/templates/protected-route/ProtectedRoute';
import { Permission } from './permissions';
import LandingPage from '@casops/routes/root/landing-page/LandingPage';
import ForgotPassword from '@casops/routes/root/forgot-password/ForgotPassword';
import NoAccess from '@casops/routes/root/no-access/NoAccess';
import { getBaseHref } from './bootstrap';
import AssetsPage from './routes/assets/assets/AssetsPage';
import PendingApprovalPage from './routes/assets/pending-approval/PendingApprovalPage';
import CertificatesPage from './routes/assets/certificates/CertificatesPage';
import AssetGroupsPage from './routes/assets/asset-groups/AssetGroupsPage';
import ConfigurationGroupsPage from './routes/configuration/configuration-groups/ConfigurationGroupsPage';
import LibraryPage from './routes/configuration/library/LibraryPage';
import UsersPage from './routes/admin/users/UsersPage';
import SettingsPage from './routes/admin/settings/SettingsPage';
import {
  bookOpenIcon,
  locationIcon,
  pendingApprovalIcon,
  pieChartIcon,
  shieldIcon,
  usersAlt,
  spannerIcon,
  vectorSquareIcon,
  lifeBuoyIcon,
} from '@cas/components/icons/icons';
import GeofenceAreas from './routes/configuration/geofence-settings/areas/GeofenceAreas';
import GeofenceStyles from './routes/configuration/geofence-settings/styles/GeofenceStyles';
import GeofenceHoldTimes from './routes/configuration/geofence-settings/hold-times/GeofenceHoldTimes';
import GeofenceSpeedLimits from './routes/configuration/geofence-settings/speed-limits/GeofenceSpeedLimits';
import Geofences from './routes/home/geofences/Geofences';
import ConfigurationGroupPackages from './routes/configuration/configuration-group/packages/ConfigurationGroupPackages';
import ConfigurationGroupAssets from './routes/configuration/configuration-group/assets/ConfigurationGroupAssets';
import ConfigurationGroupPage from './routes/configuration/configuration-group/ConfigurationGroup';
import AssetConfiguration from './routes/assets/assets/asset/configuration/AssetConfiguration';
import AssetSelfTest from './routes/assets/assets/asset/self-test/AssetSelfTest';
import AssetDetails from './routes/assets/assets/asset/details/AssetDetails';
import AssetPage from './routes/assets/assets/asset/AssetPage';
import HomePage from './routes/home/HomePage';
import Assets from './routes/home/assets/Assets';
import { useGetSiteName } from './store/slices/settingsSlice';
import GeofenceSettingsPage from './routes/configuration/geofence-settings/GeofenceSettingsPage';
import { FORGOT_PASSWORD_PAGE, RESET_PASSWORD_PAGE } from './shared/constants';
import SiteOverview from './routes/home/overview/SiteOverview';
import EditGeofence from './routes/home/geofence/edit/EditGeofence';
import CreateGeofence from './routes/home/geofence/create/CreateGeofence';
import ResetPassword from './routes/root/reset-password/ResetPassword';
import DiagnosticsPage from './routes/admin/diagnostics/DiagnosticsPage';
import AssetDiagnostics from './routes/assets/assets/asset/diagnostics/AssetDiagnostics';
import PersonnelSafetyPage from './routes/assets/personnel-safety/PersonnelSafetyPage';
import { SafetyAlertType } from './store/apis/commonModels';
import SafetyAlertsController from './routes/assets/personnel-safety/alerts/SafetyAlertsController';

export const GEOFENCE_KEY = 'geofence';
export const ASSET_KEY = 'asset';
export const HOME_OVERVIEW_GEOFENCES = '/home/overview/geofences';
export const HOME_OVERVIEW_ASSETS = '/home/overview/assets';
export const GEOFENCE_SUB_ROUTE = 'home/geofence/*';

export interface NavCategory {
  key: string;
  transKey: string; // Translation
  permission: Permission;
  links: NavLinkData[];
}

export interface NavLinkData {
  key: string;
  icon: string;
  to: string;
  permission: Permission;
  titleKey: string;
  useTitle?: () => string;
}

export const isNavCategory = (navItem: NavCategory | NavLinkData): navItem is NavCategory => {
  return 'links' in navItem;
};

// Links in the side nav. Having this here makes it easier to keep in sync with routes!
export const mainNavigation: (NavCategory | NavLinkData)[] = [
  {
    key: 'Home',
    icon: locationIcon,
    to: '/home',
    titleKey: 'Site',
    useTitle: useGetSiteName,
    permission: Permission.Viewer,
  },
  {
    key: 'Assets',
    transKey: 'NavigationCategory.Assets',
    permission: Permission.Viewer,
    links: [
      {
        key: 'Assets',
        icon: 'asset',
        to: '/assets/assets',
        titleKey: 'Navigation.Assets',
        permission: Permission.Viewer,
      },
      {
        key: 'PendingApprovals',
        icon: pendingApprovalIcon,
        to: '/assets/pending-approval',
        titleKey: 'Navigation.PendingApprovals',
        permission: Permission.AssetManagement,
      },
      {
        key: 'PersonnelSafety',
        icon: lifeBuoyIcon,
        to: '/assets/safety',
        titleKey: 'Navigation.Safety.General',
        permission: Permission.SafetyViewer,
      },
      {
        key: 'Certificates',
        icon: shieldIcon,
        to: '/assets/certificates',
        titleKey: 'Navigation.Certificates',
        permission: Permission.Admin,
      },
      {
        key: 'AssetGroups',
        icon: pieChartIcon,
        to: '/assets/asset-groups',
        titleKey: 'Navigation.AssetGroups',
        permission: Permission.AssetManagement,
      },
    ],
  },
  {
    key: 'Configuration',
    transKey: 'NavigationCategory.Configuration',
    permission: Permission.Viewer,
    links: [
      {
        key: 'ConfigurationGroups',
        icon: 'deployments',
        to: '/configuration/configuration-groups',
        titleKey: 'Navigation.ConfigurationGroups',
        permission: Permission.Viewer,
      },
      {
        key: 'Geofences',
        icon: vectorSquareIcon,
        to: '/configuration/geofence-settings',
        titleKey: 'Navigation.GeofenceSettings',
        permission: Permission.GeofenceManagement,
      },
      {
        key: 'Library',
        icon: bookOpenIcon,
        to: '/configuration/library',
        titleKey: 'Navigation.Library',
        permission: Permission.Admin,
      },
    ],
  },
  {
    key: 'Admin',
    transKey: 'NavigationCategory.Admin',
    permission: Permission.UserManagement,
    links: [
      {
        key: 'Users',
        icon: usersAlt,
        to: '/admin/users',
        titleKey: 'Navigation.Users',
        permission: Permission.UserManagement,
      },
      {
        key: 'Diagnostics',
        icon: spannerIcon,
        to: '/admin/diagnostics',
        titleKey: 'Navigation.Diagnostics',
        permission: Permission.Admin,
      },
      {
        key: 'Settings',
        icon: 'system',
        to: '/admin/settings',
        titleKey: 'Navigation.Settings',
        permission: Permission.Admin,
      },
    ],
  },
];

/*
  Home routes structure:
  /home (map)
  /home/overview (tabs)
  /home/overview/assets (assets table tab)
  /home/overview/assets?asset={assetId} (asset focused in map and table)
  /home/overview/geofences (geofences table tab)
  /home/overview/geofences?geofence={geofenceId} (geofence focused in map and table)
  /home/geofence/create (if submit successful, redirect to /home/overview/geofences)
  /home/geofence/edit/:geofenceId (if submit successful, redirect to /home/overview/geofences?geofence={geofenceId})
*/

// This catchall redirect must be used as the last child to catch any paths that haven't been matched.
const catchallRedirectToParent = { path: '*', loader: () => redirect('..') };

const homeRoute: RouteObject = {
  path: 'home',
  element: <HomePage />,
  children: [
    { index: true, loader: () => redirect('overview') },
    {
      path: 'overview',
      element: (
        <ProtectedRoute>
          <SiteOverview />
        </ProtectedRoute>
      ),
      children: [
        { index: true, loader: () => redirect('assets') },
        {
          path: 'assets',
          element: (
            <ProtectedRoute>
              <Assets />
            </ProtectedRoute>
          ),
        },
        {
          path: 'geofences',
          element: (
            <ProtectedRoute>
              <Geofences />
            </ProtectedRoute>
          ),
        },
      ],
    },
    {
      path: 'geofence/create',
      element: (
        <ProtectedRoute permission={Permission.GeofenceManagement}>
          <CreateGeofence />
        </ProtectedRoute>
      ),
    },
    {
      path: 'geofence/edit/:geofenceId',
      element: (
        <ProtectedRoute permission={Permission.GeofenceManagement}>
          <EditGeofence />
        </ProtectedRoute>
      ),
    },
    { ...catchallRedirectToParent },
  ],
};

export const navRouter = createBrowserRouter(
  [
    {
      path: '/',
      element: <Root />,
      errorElement: <ErrorPageRouter />,
      children: [
        // Routes not requiring authentication
        { index: true, element: <LandingPage /> },
        { path: FORGOT_PASSWORD_PAGE, element: <ForgotPassword /> },
        { path: RESET_PASSWORD_PAGE, element: <ResetPassword /> },

        homeRoute,

        {
          path: 'assets',
          children: [
            { index: true, loader: () => redirect('assets') },
            {
              path: 'assets',
              element: (
                <ProtectedRoute>
                  <AssetsPage />
                </ProtectedRoute>
              ),
            },
            {
              path: 'asset/:assetId',
              element: (
                <ProtectedRoute>
                  <AssetPage />
                </ProtectedRoute>
              ),
              children: [
                { index: true, loader: () => redirect('details') },
                {
                  path: 'configuration',
                  element: (
                    <ProtectedRoute>
                      <AssetConfiguration />
                    </ProtectedRoute>
                  ),
                },
                {
                  path: 'self-test',
                  element: (
                    <ProtectedRoute>
                      <AssetSelfTest />
                    </ProtectedRoute>
                  ),
                },
                {
                  path: 'details',
                  element: (
                    <ProtectedRoute>
                      <AssetDetails />
                    </ProtectedRoute>
                  ),
                },
                {
                  path: 'diagnostics',
                  element: (
                    <ProtectedRoute permission={Permission.Admin}>
                      <AssetDiagnostics />
                    </ProtectedRoute>
                  ),
                },
                { ...catchallRedirectToParent },
              ],
            },
            {
              path: 'pending-approval',
              element: (
                <ProtectedRoute permission={Permission.AssetManagement}>
                  <PendingApprovalPage />
                </ProtectedRoute>
              ),
            },
            {
              path: 'safety',
              element: (
                <ProtectedRoute permission={Permission.SafetyViewer}>
                  <PersonnelSafetyPage />
                </ProtectedRoute>
              ),
              children: [
                { index: true, loader: () => redirect('duress') },
                {
                  path: 'duress',
                  element: (
                    <ProtectedRoute permission={Permission.SafetyViewer}>
                      <SafetyAlertsController alertType={SafetyAlertType.Duress} />
                    </ProtectedRoute>
                  ),
                },
                {
                  path: 'man-down',
                  element: (
                    <ProtectedRoute permission={Permission.SafetyViewer}>
                      <SafetyAlertsController alertType={SafetyAlertType.ManDown} />
                    </ProtectedRoute>
                  ),
                },
                { ...catchallRedirectToParent },
              ],
            },
            {
              path: 'certificates',
              element: (
                <ProtectedRoute permission={Permission.Admin}>
                  <CertificatesPage />
                </ProtectedRoute>
              ),
            },
            {
              path: 'asset-groups',
              element: (
                <ProtectedRoute permission={Permission.AssetManagement}>
                  <AssetGroupsPage />
                </ProtectedRoute>
              ),
            },
            { ...catchallRedirectToParent },
          ],
        },

        {
          path: 'configuration',
          children: [
            { index: true, loader: () => redirect('configuration-groups') },
            {
              path: 'configuration-groups',
              element: (
                <ProtectedRoute>
                  <ConfigurationGroupsPage />
                </ProtectedRoute>
              ),
            },
            {
              path: 'configuration-group/:configGroupId',
              element: (
                <ProtectedRoute>
                  <ConfigurationGroupPage />
                </ProtectedRoute>
              ),
              children: [
                { index: true, loader: () => redirect('packages') },
                {
                  path: 'packages',
                  element: (
                    <ProtectedRoute>
                      <ConfigurationGroupPackages />
                    </ProtectedRoute>
                  ),
                },
                {
                  path: 'assets',
                  element: (
                    <ProtectedRoute>
                      <ConfigurationGroupAssets />
                    </ProtectedRoute>
                  ),
                },
                { ...catchallRedirectToParent },
              ],
            },
            {
              path: 'geofence-settings',
              element: (
                <ProtectedRoute permission={Permission.GeofenceManagement}>
                  <GeofenceSettingsPage />
                </ProtectedRoute>
              ),
              children: [
                { index: true, loader: () => redirect('styles') },
                {
                  path: 'styles',
                  element: (
                    <ProtectedRoute permission={Permission.GeofenceManagement}>
                      <GeofenceStyles />
                    </ProtectedRoute>
                  ),
                },
                {
                  path: 'areas',
                  element: (
                    <ProtectedRoute permission={Permission.GeofenceManagement}>
                      <GeofenceAreas />
                    </ProtectedRoute>
                  ),
                },
                {
                  path: 'speed-limits',
                  element: (
                    <ProtectedRoute>
                      <GeofenceSpeedLimits />
                    </ProtectedRoute>
                  ),
                },
                {
                  path: 'hold-times',
                  element: (
                    <ProtectedRoute>
                      <GeofenceHoldTimes />
                    </ProtectedRoute>
                  ),
                },
                { ...catchallRedirectToParent },
              ],
            },
            {
              path: 'library',
              element: (
                <ProtectedRoute permission={Permission.Admin}>
                  <LibraryPage />
                </ProtectedRoute>
              ),
            },
            { ...catchallRedirectToParent },
          ],
        },

        {
          path: 'admin',
          children: [
            { index: true, loader: () => redirect('users') },
            {
              path: 'users',
              element: (
                <ProtectedRoute permission={Permission.UserManagement}>
                  <UsersPage />
                </ProtectedRoute>
              ),
            },
            {
              path: 'diagnostics',
              element: (
                <ProtectedRoute permission={Permission.Admin}>
                  <DiagnosticsPage />
                </ProtectedRoute>
              ),
            },
            {
              path: 'settings',
              element: (
                <ProtectedRoute permission={Permission.Admin}>
                  <SettingsPage />
                </ProtectedRoute>
              ),
            },
            { ...catchallRedirectToParent },
          ],
        },

        // This 'no-access' route is used by the backend. Don't change this without a matching change in backend.
        { path: 'no-access', element: <NoAccess /> },

        // Since there is no dashboard page at the root path, a catchall redirect to the parent would take the user to the login page.
        // Instead, catch everything else that didn't match other routes and redirect to the home page.
        { path: '*', loader: () => redirect('/home') },
      ],
    },
  ],
  {
    basename: getBaseHref(),
  },
);

export const navToLandingPage = () => navRouter.navigate('/');
export const navToAssetsPage = () => navRouter.navigate('/assets');
export const navToHomePage = () => navRouter.navigate('/home');
